import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type ApiError,
  type ListPluginsResult,
  type ReviewPluginResult,
} from '@whinerp/client';
import {
  Button,
  EmptyState,
  InlineAction,
  Num,
  Panel,
  SectionLabel,
  Skeleton,
} from '@whinerp/ui';
import { formatCount } from '@whinerp/contracts/format';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';

/**
 * Setup > Apps (F-137).
 *
 * The install prompt is the feature. "It must read like a phone app permission
 * prompt: plain language, per-scope, with a refuse option." So each permission
 * is a sentence with a tick beside it, every tick starts OFF, and Install is
 * reachable with none of them ticked - which is what a refuse option actually
 * means. A screen where refusing means cancelling has not offered one.
 */
export function AppsPanel(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const errorMessage = useErrorMessage();
  const client = useQueryClient();

  const [manifestText, setManifestText] = useState('');
  const [granting, setGranting] = useState<ReadonlySet<string>>(new Set());
  const [showRuns, setShowRuns] = useState<string | null>(null);

  const plugins = useQuery<ListPluginsResult, ApiError>({
    queryKey: ['plugins'],
    queryFn: () => api.listPlugins({}),
  });

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['plugins'] });
  };

  const parsed = (): { name: string; source: string; scopes?: string[] } | null => {
    try {
      return JSON.parse(manifestText) as { name: string; source: string; scopes?: string[] };
    } catch {
      return null;
    }
  };

  const review = useMutation<ReviewPluginResult, ApiError>({
    mutationFn: () => api.reviewPlugin({ body: { manifest: parsed()! } }),
    /*
     * Every tick starts OFF.
     *
     * Pre-ticking what a plugin asked for turns consent into a formality
     * somebody clicks through - which is the entire failure a permission
     * prompt exists to prevent.
     */
    onSuccess: () => setGranting(new Set()),
  });

  const install = useMutation({
    mutationFn: () =>
      api.installPlugin({ body: { manifest: parsed()!, grant: [...granting] } }),
    onSuccess: () => {
      setManifestText('');
      review.reset();
      refresh();
    },
  });

  const toggleEnabled = useMutation({
    mutationFn: (input: { pluginId: string; enabled: boolean }) =>
      api.setPluginEnabled({ path: { pluginId: input.pluginId }, body: { enabled: input.enabled } }),
    onSuccess: refresh,
  });

  const test = useMutation({
    mutationFn: (pluginId: string) => api.testPlugin({ path: { pluginId }, body: {} }),
    onSuccess: (_result, pluginId) => {
      setShowRuns(pluginId);
      // The run that just happened is not in a list fetched before it. Without
      // this the log opens showing everything except the thing somebody
      // pressed the button to see.
      void client.invalidateQueries({ queryKey: ['plugin-runs', pluginId] });
    },
  });

  const runs = useQuery({
    queryKey: ['plugin-runs', showRuns],
    queryFn: () => api.pluginRuns({ path: { pluginId: showRuns! } }),
    enabled: showRuns !== null,
  });

  // A skeleton means "still loading". Rendering one on error is a spinner that
  // never resolves.
  if (plugins.isPending) return <Skeleton className="h-block-sm w-full" />;
  if (plugins.isError) return <></>;

  const names = (scopes: readonly string[]): string =>
    scopes
      .map((scope) => plugins.data.scopes.find((entry) => entry.key === scope)?.label ?? scope)
      .join(', ');

  return (
    <Panel data-region="apps" id="apps">
      <div className="flex flex-col gap-3">
        <SectionLabel>{t('apps.title')}</SectionLabel>
        <p className="text-meta text-ink-meta">{t('apps.body')}</p>

        <label className="flex flex-col gap-1 text-meta text-ink-meta">
          {t('apps.paste')}
          <textarea
            data-app-manifest
            rows={4}
            value={manifestText}
            onChange={(event) => setManifestText(event.target.value)}
            className="rounded-chip border border-line bg-surface p-2 font-mono text-dense text-ink"
          />
        </label>
        <div>
          <Button
            data-app-review
            intent="secondary"
            disabled={parsed() === null || review.isPending}
            onClick={() => review.mutate()}
          >
            {t('apps.review')}
          </Button>
        </div>

        {/*
          The prompt.

          Per-scope, in sentences, with Install reachable while every tick is
          off. A screen where refusing means cancelling has not offered a
          refuse option.
        */}
        {review.data ? (
          <div
            data-app-prompt
            className="flex flex-col gap-2 rounded-card border border-line bg-sunken p-3"
          >
            <p className="text-body text-ink">
              {t('apps.asking', {
                values: { name: review.data.name, publisher: review.data.publisher },
              })}
            </p>
            {review.data.asks.length === 0 ? (
              <p data-app-asks-nothing className="text-meta text-ink-meta">
                {t('apps.nothingAsked')}
              </p>
            ) : (
              <ul className="flex flex-col gap-2" data-app-asks>
                {review.data.asks.map((ask) => (
                  <li key={ask.scope} data-app-ask={ask.scope} className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      data-app-grant={ask.scope}
                      className="mt-1"
                      checked={granting.has(ask.scope)}
                      onChange={() =>
                        setGranting((held) => {
                          const next = new Set(held);
                          if (next.has(ask.scope)) next.delete(ask.scope);
                          else next.add(ask.scope);
                          return next;
                        })
                      }
                    />
                    <span className="flex flex-col">
                      <span className="text-body text-ink">{ask.label}</span>
                      {/* What it really means, not what it is called. */}
                      <span className="text-meta text-ink-meta">{ask.detail}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {/* Named, not dropped: a manifest asking for something this
                product has never heard of was built for another version. */}
            {review.data.unrecognisedScopes.length > 0 ? (
              <p data-app-unknown className="text-meta text-warning-text">
                {t('apps.unknown', { values: { scopes: review.data.unrecognisedScopes.join(', ') } })}
              </p>
            ) : null}
            <div className="flex flex-wrap gap-2">
              <Button
                data-app-install
                intent="primary"
                busy={install.isPending}
                onClick={() => install.mutate()}
              >
                {t('apps.install')}
              </Button>
              <InlineAction data-app-cancel onClick={() => review.reset()}>
                {t('apps.cancel')}
              </InlineAction>
            </div>
            <p className="text-meta text-ink-meta">{t('apps.installedOff')}</p>
            {install.isError ? (
              <p role="alert" data-app-error className="text-meta text-danger">
                {errorMessage(install.error)}
              </p>
            ) : null}
          </div>
        ) : null}

        {plugins.data.plugins.length === 0 ? (
          <EmptyState
            data-app-empty
            illustration="Package"
            title={t('apps.title')}
            body={t('apps.empty')}
            primary={{ label: t('apps.review'), icon: 'Plus', href: '/setup#apps' }}
          />
        ) : (
          <ul className="flex flex-col gap-2" data-app-list>
            {plugins.data.plugins.map((plugin) => (
              <li
                key={plugin.pluginId}
                data-app={plugin.pluginId}
                data-app-status={plugin.status}
                className="flex flex-col gap-1 rounded-chip bg-sunken px-3 py-2"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-body text-ink">{plugin.name}</span>
                  <span className="text-meta text-ink-meta">
                    {plugin.publisher} · {plugin.version}
                  </span>
                  <Num className="text-meta text-ink-meta">
                    {t('apps.limits', {
                      values: {
                        ms: formatCount(plugin.timeoutMs, format),
                        mb: formatCount(plugin.memoryMb, format),
                      },
                    })}
                  </Num>
                  <InlineAction
                    data-app-toggle={plugin.pluginId}
                    onClick={() =>
                      toggleEnabled.mutate({
                        pluginId: plugin.pluginId,
                        enabled: plugin.status !== 'enabled',
                      })
                    }
                  >
                    {plugin.status === 'enabled' ? t('apps.disable') : t('apps.enable')}
                  </InlineAction>
                  <InlineAction data-app-test={plugin.pluginId} onClick={() => test.mutate(plugin.pluginId)}>
                    {t('apps.test')}
                  </InlineAction>
                  <InlineAction data-app-runs={plugin.pluginId} onClick={() => setShowRuns(plugin.pluginId)}>
                    {t('apps.runs')}
                  </InlineAction>
                </div>
                <span data-app-granted className="text-meta text-ink-meta">
                  {plugin.granted.length === 0
                    ? t('apps.noneGranted')
                    : t('apps.granted', { values: { scopes: names(plugin.granted) } })}
                </span>
                {/* Kept visible, so a later review can tell a refusal from a
                    plugin that never asked. */}
                {plugin.refused.length > 0 ? (
                  <span data-app-refused className="text-meta text-ink-meta">
                    {t('apps.refused', { values: { scopes: names(plugin.refused) } })}
                  </span>
                ) : null}
                {plugin.disabledReason ? (
                  <span data-app-disabled-reason className="text-meta text-warning-text">
                    {plugin.disabledReason}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        )}

        {/*
          A run that could not even start says so.

          Without this, pressing "Run it once" and getting nothing is
          indistinguishable from a plugin that did nothing - and the button
          looks wired to nothing.
        */}
        {test.isError ? (
          <p role="alert" data-app-test-error className="text-meta text-danger">
            {errorMessage(test.error)}
          </p>
        ) : null}
        {showRuns && runs.isError ? (
          <p role="alert" data-app-runs-error className="text-meta text-danger">
            {errorMessage(runs.error)}
          </p>
        ) : null}
        {showRuns && runs.data ? (
          <div className="flex flex-col gap-1" data-app-run-log>
            <SectionLabel>{t('apps.runs')}</SectionLabel>
            {runs.data.runs.length === 0 ? (
              <p data-app-no-runs className="text-meta text-ink-meta">
                {t('apps.noRuns')}
              </p>
            ) : (
              <ul className="flex flex-col">
                {runs.data.runs.map((run) => (
                  <li
                    key={run.runId}
                    data-app-run={run.outcome}
                    className="flex flex-wrap gap-2 text-meta text-ink-meta"
                  >
                    <span className="text-ink">
                      {t(`apps.outcome.${run.outcome}` as 'apps.outcome.ok')}
                    </span>
                    <span>{run.trigger}</span>
                    <Num>{formatCount(run.durationMs, format)}</Num>
                    {/* Contained: a plugin's failure is a line in its own log,
                        not something the host had to survive. */}
                    {run.error ? <span className="text-danger">{run.error}</span> : null}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}
      </div>
    </Panel>
  );
}
